import { useEffect, useRef, useState, type FormEvent } from "react";
import { InlineSpinner } from "../components/InlineSpinner";
import { ApiError, apiRequest } from "../lib/api";
import { navigate } from "../lib/navigation";
import { InlineError } from "../components/StudioShell";
import type { SessionKind } from "@studio/shared";

interface JoinValidation {
  ok: true;
  title: string;
  humming_enabled: boolean;
  kind: SessionKind;
}

const joinContextKey = "mvs:join-context";

export function JoinPage() {
  const queryCode = new URLSearchParams(window.location.search).get("code") ?? "";
  const [code, setCode] = useState(queryCode);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const autoValidated = useRef(false);

  const validate = async (event?: FormEvent): Promise<void> => {
    event?.preventDefault();
    const normalized = code.replace(/\D/gu, "").slice(0, 6);
    setCode(normalized);
    if (normalized.length !== 6) {
      setError("6자리 수업 코드를 입력해 주세요.");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const result = await apiRequest<JoinValidation>("/api/join/validate", {
        method: "POST",
        authenticated: false,
        body: JSON.stringify({ code: normalized }),
      });
      sessionStorage.setItem(joinContextKey, JSON.stringify({ code: normalized, ...result }));
      navigate(`/join/name?code=${encodeURIComponent(normalized)}`);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "수업을 확인하지 못했습니다.");
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    const normalized = queryCode.replace(/\D/gu, "").slice(0, 6);
    if (normalized.length === 6 && !autoValidated.current) {
      autoValidated.current = true;
      setCode(normalized);
      void validate();
    }
  }, []);

  return (
    <main className="join-page">
      <section className="join-card">
        <img className="brand-mark" src="/favicon.png" alt="teachermate 스튜디오" width={44} height={44} />
        <h1 tabIndex={-1}>수업 참여하기</h1>
        <p className="join-subtitle">선생님이 알려준 6자리 코드를 입력하세요</p>
        <form onSubmit={(event) => void validate(event)}>
          <label className="sr-only" htmlFor="session-code">수업 코드</label>
          <input
            id="session-code"
            className="join-code-input"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={6}
            autoComplete="one-time-code"
            autoFocus
            value={code}
            onChange={(event) => setCode(event.target.value)}
            onBlur={() => setCode((value) => value.replace(/\D/gu, "").slice(0, 6))}
            aria-invalid={Boolean(error)}
            disabled={submitting}
          />
          {error ? <InlineError>{error}</InlineError> : null}
          <button className="button-primary join-primary" type="submit" disabled={submitting || code.length !== 6}>
            {submitting ? <InlineSpinner label="수업 확인 중..." /> : "다음"}
          </button>
        </form>
        <button className="button-ghost" type="button" onClick={() => navigate("/")}>
          돌아가기
        </button>
        <p className="join-hint">QR이나 링크로 들어오면 코드가 자동으로 채워져요</p>
      </section>
    </main>
  );
}
